import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  FileText, Workflow, Zap, Sparkles, ArrowRight,
  Upload, CheckCircle, Download, Clock, Users, Shield
} from 'lucide-react';
import PublicHeader from '@/components/PublicHeader';

export default function HowItWorksPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <PublicHeader breadcrumbs={[{ label: 'How It Works' }]} />

      <div className="pt-24 sm:pt-32">
      {/* Hero Section */}
      <section 
        className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-violet-600 to-indigo-600 relative overflow-hidden px-4"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_courts-module/artifacts/4yacjm1o_header.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-violet-600/30 to-indigo-600/30"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6">
            How TwentyDocs Works
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-violet-100 max-w-3xl mx-auto mb-6 sm:mb-8">
            Transform your document workflow in minutes. No technical expertise required.
          </p>
          <div className="flex justify-center gap-4">
            <Button
              onClick={() => navigate('/auth')}
              size="lg"
              className="h-12 sm:h-14 px-8 sm:px-10 rounded-full bg-white text-violet-600 hover:bg-slate-100 text-base sm:text-lg font-bold"
            >
              Get Started Free
              <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">
              From Document to Data in 3 Simple Steps
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto">
              TwentyDocs eliminates the tedious work of creating personalized documents. 
              Upload once, reuse forever.
            </p>
          </div>

          {/* Main Workflow */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {/* Step 1: Upload */}
            <div className="relative h-full">
              <div className="bg-white rounded-2xl sm:rounded-3xl border-2 border-violet-200 p-6 sm:p-8 flex flex-col h-full">
                <div className="mb-4 sm:mb-6 rounded-lg sm:rounded-xl overflow-hidden bg-gradient-to-br from-blue-50 to-violet-50 p-3 sm:p-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_courts-module/artifacts/rwuhpg3s_step1.jpg"
                    alt="Upload Your Document"
                    className="w-full h-auto rounded-lg"
                  />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3">Upload Your Document</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-3 sm:mb-4 flex-grow">
                  Simply upload your Word document with placeholders like <code className="px-2 py-1 bg-slate-100 rounded text-xs sm:text-sm">{'{client_name}'}</code> or <code className="px-2 py-1 bg-slate-100 rounded text-xs sm:text-sm">{'{invoice_date}'}</code>
                </p>
                <Button
                  variant="link"
                  onClick={() => navigate('/features/smart-document-parsing')}
                  className="text-violet-600 p-0 h-auto self-start text-sm sm:text-base"
                >
                  Learn about Smart Parsing →
                </Button>
              </div>
            </div>

            {/* Step 2: Build Form */}
            <div className="relative h-full">
              <div className="bg-white rounded-2xl sm:rounded-3xl border-2 border-blue-200 p-6 sm:p-8 flex flex-col h-full">
                <div className="mb-4 sm:mb-6 rounded-lg sm:rounded-xl overflow-hidden bg-gradient-to-br from-blue-50 to-violet-50 p-3 sm:p-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_courts-module/artifacts/bosotkjr_step2.jpg"
                    alt="Auto-Generated Form"
                    className="w-full h-auto rounded-lg"
                  />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3">Auto-Generated Form</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-3 sm:mb-4 flex-grow">
                  TwentyDocs automatically creates a beautiful form from your template. Customize field types, add validation, set defaults.
                </p>
                <Button
                  variant="link"
                  onClick={() => navigate('/features/drag-drop-form-builder')}
                  className="text-blue-600 p-0 h-auto self-start text-sm sm:text-base"
                >
                  Explore Form Builder →
                </Button>
              </div>
            </div>

            {/* Step 3: Generate */}
            <div className="relative h-full">
              <div className="bg-white rounded-2xl sm:rounded-3xl border-2 border-green-200 p-6 sm:p-8 flex flex-col h-full">
                <div className="mb-4 sm:mb-6 rounded-lg sm:rounded-xl overflow-hidden bg-gradient-to-br from-blue-50 to-violet-50 p-3 sm:p-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_courts-module/artifacts/mganbh4z_step3.jpg"
                    alt="Generate Documents"
                    className="w-full h-auto rounded-lg"
                  />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 sm:mb-3">Generate Documents</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-3 sm:mb-4 flex-grow">
                  Fill the form with your data and instantly generate personalized documents. Export as PDF or DOCX.
                </p>
                <Button
                  variant="link"
                  onClick={() => navigate('/features/instant-mail-merge')}
                  className="text-green-600 p-0 h-auto self-start text-sm sm:text-base"
                >
                  See Mail Merge in Action →
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Showcase with Images */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">See It In Action</h2>
            <p className="text-base sm:text-lg text-slate-600">
              Visual examples of how TwentyDocs transforms your workflow
            </p>
          </div>

          {/* Feature 1: Document Parsing */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16 md:mb-20">
            <div className="order-2 md:order-1">
              <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-violet-100 text-violet-700 text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                Step 1: Upload
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4">Smart Document Parsing</h3>
              <p className="text-base sm:text-lg text-slate-600 mb-4 sm:mb-6">
                Upload any Word document and watch as TwentyDocs automatically identifies all your template fields. 
                No manual configuration needed.
              </p>
              <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <li className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-sm sm:text-base text-slate-700">Recognizes <code className="px-2 py-1 bg-slate-100 rounded text-xs sm:text-sm">{'{field}'}</code> placeholders</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-sm sm:text-base text-slate-700">Preserves all formatting and styling</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-sm sm:text-base text-slate-700">Works with complex documents</span>
                </li>
              </ul>
              <Button
                variant="outline"
                onClick={() => navigate('/features/smart-document-parsing')}
                className="rounded-xl text-sm sm:text-base"
              >
                Learn More About Smart Parsing
                <ArrowRight className="ml-2 w-3 h-3 sm:w-4 sm:h-4" />
              </Button>
            </div>
            <div className="order-1 md:order-2 bg-gradient-to-br from-blue-50 to-violet-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-violet-200">
              <img 
                src="https://customer-assets.emergentagent.com/job_courts-module/artifacts/cnigja5d_smart-document-parsing.jpg"
                alt="Smart Document Parsing"
                className="w-full h-auto rounded-lg sm:rounded-xl shadow-lg"
              />
            </div>
          </div>

          {/* Feature 2: Form Builder */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16 md:mb-20">
            <div className="bg-gradient-to-br from-blue-50 to-violet-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-200">
              <img 
                src="https://customer-assets.emergentagent.com/job_courts-module/artifacts/ea2crrkj_drag-drop-form.jpg"
                alt="Drag & Drop Form Builder"
                className="w-full h-auto rounded-lg sm:rounded-xl shadow-lg"
              />
            </div>
            <div>
              <div className="inline-block px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold mb-4">
                Step 2: Customize
              </div>
              <h3 className="text-3xl font-bold text-slate-900 mb-4">Drag & Drop Form Builder</h3>
              <p className="text-lg text-slate-600 mb-6">
                Customize your auto-generated form with our intuitive visual builder. 
                Add validation, set defaults, configure field types - all without code.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">Drag fields to reorder</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">Configure validation rules</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">Live preview as you build</span>
                </li>
              </ul>
              <Button
                variant="outline"
                onClick={() => navigate('/features/drag-drop-form-builder')}
                className="rounded-xl"
              >
                Explore Form Builder
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Feature 3: Mail Merge */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div className="order-2 md:order-1">
              <div className="inline-block px-4 py-2 rounded-full bg-green-100 text-green-700 text-sm font-semibold mb-4">
                Step 3: Generate
              </div>
              <h3 className="text-3xl font-bold text-slate-900 mb-4">Instant Mail Merge</h3>
              <p className="text-lg text-slate-600 mb-6">
                Fill your form with real data and generate personalized documents instantly. 
                Create hundreds of documents in seconds with batch processing.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">One-click document generation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">Export as PDF or DOCX</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">Batch process multiple records</span>
                </li>
              </ul>
              <Button
                variant="outline"
                onClick={() => navigate('/features/instant-mail-merge')}
                className="rounded-xl"
              >
                See Mail Merge Details
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            <div className="order-1 md:order-2 bg-gradient-to-br from-blue-50 to-violet-50 rounded-2xl p-6 border border-green-200">
              <img 
                src="https://customer-assets.emergentagent.com/job_courts-module/artifacts/3hj7nols_ms-word-style-mail-merge.jpg"
                alt="Instant Mail Merge"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>

          {/* Feature 4: Template Management */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="bg-gradient-to-br from-blue-50 to-violet-50 rounded-2xl p-6 border border-violet-200">
              <img 
                src="https://customer-assets.emergentagent.com/job_courts-module/artifacts/a0qqdf54_templates.jpg"
                alt="Template Management"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
            <div>
              <div className="inline-block px-4 py-2 rounded-full bg-orange-100 text-orange-700 text-sm font-semibold mb-4">
                Step 4: Reuse
              </div>
              <h3 className="text-3xl font-bold text-slate-900 mb-4">Template Management</h3>
              <p className="text-lg text-slate-600 mb-6">
                Save your templates and reuse them unlimited times with different data sets. 
                Organize with categories, track usage, and share with your team.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">Unlimited template runs</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">Organize with categories</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">Version history & tracking</span>
                </li>
              </ul>
              <Button
                variant="outline"
                onClick={() => navigate('/features/template-management')}
                className="rounded-xl"
              >
                Discover Template Management
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-violet-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Choose TwentyDocs?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Save time, reduce errors, and scale your document operations
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-slate-200">
              <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mb-6">
                <Clock className="w-8 h-8 text-violet-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Save 10+ Hours/Week</h3>
              <p className="text-slate-600">
                Eliminate manual document creation. Generate hundreds of personalized documents in minutes instead of days.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200">
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">100% Accurate</h3>
              <p className="text-slate-600">
                No more typos or data entry errors. Automated merging ensures every document is perfect every time.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-200">
              <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Team Collaboration</h3>
              <p className="text-slate-600">
                Share templates across your organization. Multiple team members can use the same templates simultaneously.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Perfect For</h2>
            <p className="text-lg text-slate-600">
              Any industry that needs to generate personalized documents at scale
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Law Firms', desc: 'Contracts, court filings, client letters' },
              { title: 'HR Teams', desc: 'Offer letters, onboarding docs, policies' },
              { title: 'Sales Teams', desc: 'Proposals, quotes, agreements' },
              { title: 'Healthcare', desc: 'Patient forms, consent documents' },
              { title: 'Real Estate', desc: 'Leases, disclosures, agreements' },
              { title: 'Finance', desc: 'Reports, statements, invoices' },
              { title: 'Education', desc: 'Certificates, transcripts, letters' },
              { title: 'Small Business', desc: 'Invoices, contracts, correspondence' }
            ].map((useCase, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-slate-200 hover:border-violet-300 transition-colors">
                <h4 className="font-bold text-slate-900 mb-2">{useCase.title}</h4>
                <p className="text-sm text-slate-600">{useCase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-violet-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Workflow?
          </h2>
          <p className="text-xl text-violet-100 mb-8">
            Join thousands of professionals who've automated their document processes with TwentyDocs
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              onClick={() => navigate('/auth')}
              size="lg"
              className="h-14 px-10 rounded-full bg-white text-violet-600 hover:bg-slate-100 text-lg font-bold"
            >
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => navigate('/pricing')}
              size="lg"
              variant="outline"
              className="h-14 px-10 rounded-full border-2 border-white text-white hover:bg-white/10 text-lg font-bold"
            >
              View Pricing
            </Button>
          </div>
          <p className="text-sm text-violet-200 mt-6">
            No credit card required • Free forever plan available
          </p>
        </div>
      </section>
      </div>
    </div>
  );
}
